import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js"; // Reused component
import Features from "../../../../Components/Features/ThreeColumnPages"; // Reused component
import {
  FaCheckCircle,
  FaUserTie,
  FaUsers,
  FaHandshake,
  FaAward,
  FaMapMarkerAlt,
  FaChartLine,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

// UI/UX Usability Testing in Dubai Page Component
export const UIUXUsabilityTestingInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="UI/UX Usability Testing in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={
          <Subheading>
            Optimize Your Digital Products with Confidence
          </Subheading>
        }
        heading={
          <Heading>
            Elevate User Experience with{" "}
            <span tw="text-primary-500">UI/UX Usability Testing in Dubai</span>
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we specialize in delivering comprehensive UI/UX
            usability testing services tailored to businesses across **Dubai**.
            Our team combines expertise, innovation, and data-driven insights to
            ensure your digital products are intuitive, engaging, and optimized
            for success.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // User testing session
          "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // UI/UX analysis
          "https://images.unsplash.com/photo-1605379399642-870262d3d051?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Data analytics visualization
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Get Started Today"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Your Trusted Partner for UI/UX Usability Testing in Dubai"
        description="We combine expertise, creativity, and innovation to deliver unmatched value."
        cards={[
          {
            icon: <FaCheckCircle color="#ff5464" size={40} />,
            title: "Comprehensive Testing",
            description:
              "Identify usability issues and optimize user interactions effectively.",
          },
          {
            icon: <FaUserTie color="#ff5464" size={40} />,
            title: "Expert Analysis",
            description:
              "Leverage our team's deep expertise in usability testing methodologies.",
          },
          {
            icon: <FaMapMarkerAlt color="#ff5464" size={40} />,
            title: "Localized Expertise",
            description:
              "Understand Dubai's unique market trends and diverse demographics.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "User-Centric Approach",
            description:
              "Prioritize usability, accessibility, and engagement for all users.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client-Centric Collaboration",
            description:
              "Work closely with you to ensure alignment with your vision and goals.",
          },
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Proven Track Record",
            description:
              "Delivering exceptional results for Dubai clients since [Year].",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Streamlined Process for UI/UX Usability Testing in Dubai"
        description={
          <Description>
            Our Dubai-focused process ensures quality, efficiency, and
            measurable results:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Discovery & Research: Understand your business, audience, and
                goals.
              </li>
              <li>
                User Analysis: Identify key user personas and their preferences.
              </li>
              <li>
                Test Planning: Develop a detailed usability testing plan
                tailored to your needs.
              </li>
              <li>
                Testing Execution: Conduct tests with real users to gather
                actionable insights.
              </li>
              <li>
                Data Analysis: Analyze results to identify pain points and
                opportunities.
              </li>
              <li>
                Recommendations: Provide actionable recommendations to improve
                usability.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // User behavior analysis
          "https://images.unsplash.com/photo-1535957998253-26ae1ef29506?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Testing devices setup
          "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Usability report analysis
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Results That Speak for Themselves"
        description="Here’s how our UI/UX usability testing services have empowered businesses in Dubai."
        cards={[
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Improved User Engagement",
            description:
              "Helped an e-commerce platform in Dubai increase user retention by 150% through strategic usability improvements.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description:
              "98% client retention rate through consistent excellence and measurable results.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Performance Boost",
            description:
              "Achieved a 200% increase in conversions for a retail app using optimized UX strategies.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={
          <Subheading>Ready to Optimize Your Digital Product?</Subheading>
        }
        heading="Let’s Create Impactful Solutions Together in Dubai"
        description={
          <Description>
            Partner with Dubai’s leading UI/UX usability testing experts to
            unlock your product’s full potential. Whether you’re launching a
            website, mobile app, or software, we’re here to help.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1520333789090-1afc82db536a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Team analyzing results
          "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Dubai tech business district
          "https://images.unsplash.com/photo-1556155092-490a1ba16284?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // User experience improvement
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Today"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
